import { FC, FunctionComponent, useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@shared/utils";
import { QueryResult } from "@apollo/client/react";

import { Box, Button, Typography } from "@mui/material";
import {
  isCompatibleCategory,
  isParentCompatible,
  SubCategoryMap,
} from "../../../utils/options";
import { groupByCategory } from "../../../utils/group";
import { RemoveCategory } from "../../../pages/dialogs/RemoveCategory";
import { IcIcon } from "@shared/components";
import { QueryShapeOutput } from "@shared/core-utils";
import {
  CategoryData,
  OptionListItem,
  ProductOptionsData,
} from "../ProductOption";

export const OptionsTab: FC<{
  categorys: CategoryData[];
  categoryQuery: QueryResult<{
    categorys?: CategoryData[];
  }>;
  productConfig: ProductOptionsData;
  onAddOption: (category: CategoryData) => void;
  parentProductConfigurationQuery?: QueryResult<{
    productConfiguration?: ProductOptionsData;
  }>;
  productConfigurationQuery?: QueryResult<{
    productConfiguration?: ProductOptionsData;
  }>;
  loading?: boolean;
  shapeData?: QueryResult<QueryShapeOutput>;
  catmap?: SubCategoryMap;
  parentCategoriesIds?: string[];
  selectedCategories?: string[];
  setShowRemove?: (category: string) => void;
  setActiveLoadingCategory?: (id: string) => void;
  activeLoadingCategory?: string | undefined;
}> = ({
  categorys,
  productConfig,
  onAddOption,
  categoryQuery,
  parentProductConfigurationQuery,
  productConfigurationQuery,
  loading = false,
  shapeData,
  catmap = { submap: new Map(), selectedOptions: [] },
  parentCategoriesIds = [],
  selectedCategories = [],
  setActiveLoadingCategory = () => {},
  activeLoadingCategory,
}) => {
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [categoryToRemove, setCategoryToRemove] = useState<string | null>(null);

  const groupedOptions = useMemo(() => {
    const optionsList =
      productConfigurationQuery?.data?.productConfiguration?.OptionsList || [];
    return groupByCategory(optionsList as OptionListItem[]).filter(
      (g) => !parentCategoriesIds.includes(g.category?._id)
    );
  }, [
    productConfigurationQuery?.data?.productConfiguration?.OptionsList,
    parentCategoriesIds,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 5,
        }}
      >
        {categorys
          ?.slice()
          .sort((a, b) => (a?.name > b?.name ? 1 : -1))
          .map((c) => {
            const isLoading =
              categoryQuery.loading ||
              (parentProductConfigurationQuery?.loading ?? false) ||
              (productConfigurationQuery?.loading ?? false) ||
              loading ||
              (shapeData?.loading ?? false);
            const isCompatible = isCompatibleCategory(
              catmap,
              c._id,
              categorys as CategoryData[]
            );

            const isParentCompatibleValid = isParentCompatible({
              category: c,
              parentCategorys: parentCategoriesIds,
            });
            const isDisabled = !isCompatible || !isParentCompatibleValid;

            return (
              <Box
                key={c._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography
                  color={isDisabled ? "textSecondary" : "inherit"}
                  sx={{ fontSize: 16 }}
                >
                  {c.name}
                </Typography>
                {selectedCategories?.includes(c._id) ? (
                  <Button
                    variant="contained"
                    disabled={isDisabled}
                    color="error"
                    onClick={() => {
                      setCategoryToRemove(c._id);
                      setShowRemoveDialog(true);
                    }}
                  >
                    Delete
                  </Button>
                ) : (
                  <Button
                    disabled={isLoading || isDisabled}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      onAddOption(c);
                      setActiveLoadingCategory(c._id);
                    }}
                  >
                    {isLoading && c._id === activeLoadingCategory ? (
                      <IcIcon icon="line-md:loading-loop" />
                    ) : (
                      "Add"
                    )}
                  </Button>
                )}
              </Box>
            );
          })}
      </Box>
      {productConfigurationQuery?.data?.productConfiguration?._id &&
        categoryToRemove && (
          <RemoveCategory
            open={showRemoveDialog}
            productConfigurationId={
              productConfigurationQuery.data.productConfiguration._id
            }
            removeCategoryId={categoryToRemove}
            groupedOptions={groupedOptions}
            onClose={() => {
              setShowRemoveDialog(false);
              setCategoryToRemove(null);
            }}
          />
        )}
    </>
  );
};
